/**
 * useMagicLinkError — detect Supabase magic-link expiry hash params and
 * navigate to /link-expired.
 *
 * Supabase appends an error fragment to the redirect URL when a magic link
 * has expired or been used:
 *   #error=access_denied&error_code=otp_expired&error_description=...
 * The hash sits there until something clears it. We sniff on mount, route
 * to /link-expired, then strip the hash so a refresh does not loop.
 *
 * Wouter uses hash-based routing in this app (useHashLocation), which means
 * the path lives in `window.location.hash` after `#`. The Supabase params
 * are also hash params, but on a separate query-string under the same `#`.
 * We look for `error=access_denied` anywhere in the hash regardless of order.
 */
import { useEffect } from "react";

export function useMagicLinkError(): void {
  useEffect(() => {
    const h = window.location.hash || "";
    if (!h) return;
    const lower = h.toLowerCase();
    const isAccessDenied = lower.includes("error=access_denied");
    const isExpired =
      lower.includes("otp_expired") ||
      lower.includes("expired") ||
      lower.includes("link_expired");
    if (!isAccessDenied && !isExpired) return;

    // Clear the hash so a refresh on /link-expired does not loop.
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch {
      /* ignore */
    }
    // Navigate via hash so wouter's hash-location picks it up.
    window.location.hash = "#/link-expired";
  }, []);
}
